import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Star, User, LogOut, Settings, Menu, X, Trophy, Award, Crown,
  Utensils, MapPin, Compass, Gem, Globe, Plane, ChefHat, Sparkles,
  MessageSquare, PenLine, Users, Camera, Lock, CheckCircle, Earth
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ICON_MAP = {
  "star": Star,
  "stars": Sparkles,
  "crown": Crown,
  "trophy": Trophy,
  "utensils": Utensils,
  "map-pin": MapPin,
  "compass": Compass,
  "gem": Gem,
  "award": Award,
  "globe": Globe,
  "plane": Plane,
  "earth": Earth,
  "chef-hat": ChefHat,
  "sparkles": Sparkles,
  "message-square": MessageSquare,
  "pen": PenLine,
  "users": Users,
  "camera": Camera,
};

const Achievements = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [unlockedCount, setUnlockedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchAchievements();
  }, []);

  const fetchAchievements = async () => {
    try {
      const response = await axios.get(`${API}/achievements/me`, { withCredentials: true });
      setAchievements(response.data.achievements);
      setUnlockedCount(response.data.unlocked_count);
      setTotalCount(response.data.total_count);
    } catch (error) {
      console.warn("Error fetching achievements:", error);
    } finally {
      setLoading(false);
    }
  };

  const checkForNewAchievements = async () => {
    setChecking(true);
    try {
      const response = await axios.post(`${API}/achievements/check`, {}, { withCredentials: true });
      if (response.data.count > 0) {
        response.data.new_achievements.forEach(a => {
          toast.success(`🏆 ${t('achievements.newUnlock')}: ${a.name}`);
        });
        fetchAchievements();
      } else {
        toast.info("No new achievements unlocked");
      }
    } catch (error) {
      toast.error("Failed to check achievements");
    } finally {
      setChecking(false);
    }
  };

  const navItems = [
    { label: t('common.dashboard'), path: '/dashboard' },
    { label: t('common.restaurants'), path: '/restaurants' },
    { label: t('common.preferences'), path: '/preferences' },
    { label: t('common.community'), path: '/community' },
    { label: t('common.leaderboard'), path: '/leaderboard' },
  ];

  const unlockedAchievements = achievements.filter(a => a.unlocked);
  const lockedAchievements = achievements.filter(a => !a.unlocked);
  const progressPercent = totalCount > 0 ? (unlockedCount / totalCount) * 100 : 0;

  return (
    <div className="min-h-screen bg-background" data-testid="achievements-page">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/dashboard')}>
              <Star className="w-6 h-6 text-primary fill-primary" />
              <span className="text-xl font-bold gradient-text">Étoile</span>
            </div>

            <nav className="hidden md:flex items-center gap-6">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </nav>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    {user?.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <User className="w-4 h-4 text-primary" />
                      </div>
                    )}
                    <span className="hidden sm:inline text-sm">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card border-border">
                  <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                    <Settings className="w-4 h-4 mr-2" />
                    {t('common.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-muted/50" />
                  <DropdownMenuItem onClick={() => logout()} className="cursor-pointer text-red-400">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t('common.logout')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-border">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight mb-2 flex items-center gap-3">
              <Trophy className="w-8 h-8 text-primary" />
              {t('achievements.title')}
            </h1>
            <p className="text-muted-foreground">{t('achievements.subtitle')}</p>
          </div>
          <Button 
            onClick={checkForNewAchievements}
            disabled={checking}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {checking ? "Checking..." : t('achievements.checkAchievements')}
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {/* Progress Card */}
            <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20 mb-8">
              <CardContent className="py-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-muted-foreground">{t('achievements.progress')}</p>
                    <p className="text-2xl font-bold">
                      <span className="gradient-text">{unlockedCount}</span>
                      <span className="text-muted-foreground"> / {totalCount}</span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold gradient-text">{Math.round(progressPercent)}%</p>
                  </div>
                </div>
                <Progress value={progressPercent} className="h-3" />
              </CardContent>
            </Card>

            {/* Unlocked Achievements */}
            {unlockedAchievements.length > 0 && (
              <div className="mb-8">
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  {t('achievements.unlocked')} ({unlockedAchievements.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {unlockedAchievements.map((achievement) => {
                    const IconComponent = ICON_MAP[achievement.icon] || Trophy;
                    return (
                      <Card 
                        key={achievement.id} 
                        className="bg-card border-primary/30 hover:border-primary/50 transition-colors"
                        data-testid={`achievement-${achievement.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-primary/20 rounded-lg">
                              <IconComponent className="w-6 h-6 text-primary" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-sm">{achievement.name}</h3>
                              <p className="text-xs text-muted-foreground mt-1">
                                {achievement.description}
                              </p>
                            </div>
                            <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Locked Achievements */}
            {lockedAchievements.length > 0 && (
              <div>
                <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  <Lock className="w-5 h-5 text-muted-foreground" />
                  {t('achievements.locked')} ({lockedAchievements.length})
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {lockedAchievements.map((achievement) => {
                    const IconComponent = ICON_MAP[achievement.icon] || Trophy;
                    return (
                      <Card 
                        key={achievement.id} 
                        className="bg-card/50 border-border opacity-60"
                        data-testid={`achievement-${achievement.id}-locked`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="p-2 bg-muted/20 rounded-lg">
                              <IconComponent className="w-6 h-6 text-muted-foreground" />
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-sm text-muted-foreground">
                                {achievement.name}
                              </h3>
                              <p className="text-xs text-muted-foreground/70 mt-1">
                                {achievement.description}
                              </p>
                            </div>
                            <Lock className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default Achievements;
